/**
 * DrillDownSheet — slide-over panel that shows the filtered list driving a chart segment.
 *
 * Props:
 *  - open, onClose
 *  - title
 *  - subtitle  (e.g. "127 results")
 *  - fetchUrl    (e.g. "/api/data/suppliers?status=Approved&page_size=200")
 *  - columns     [{ key, label, format? }]
 *  - dataPath    'data' | 'items'  — where the array lives in the response (default 'data')
 */
import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import api from '../../services/api';
import { LoadingSpinner } from '../../components/layout/SharedComponents';
import { ExternalLink, X } from 'lucide-react';

export default function DrillDownSheet({ open, onClose, title, subtitle, fetchUrl, columns = [], dataPath = 'data', linkTo, emptyHint, filter }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const filterKey = JSON.stringify(filter || {});

  useEffect(() => {
    if (!open || !fetchUrl) return;
    setLoading(true);
    setError(null);
    api.get(fetchUrl)
      .then(res => {
        let arr = Array.isArray(res.data) ? res.data : res.data?.[dataPath] || res.data?.items || res.data?.results || [];
        // client-side filter when backend doesn't support the query param
        if (filter && filter.key) {
          arr = arr.filter(r => {
            const v = r[filter.key];
            if (v == null) return false;
            return String(v).toLowerCase() === String(filter.value).toLowerCase();
          });
        }
        setRows(arr);
      })
      .catch(e => { setError(e?.response?.data?.detail || 'Failed to load'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchUrl, dataPath, filterKey]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-[640px] sm:max-w-2xl overflow-y-auto" data-testid="drill-down-sheet">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base">{title}</SheetTitle>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="drill-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>
          {subtitle && <SheetDescription className="text-xs">{subtitle}</SheetDescription>}
        </SheetHeader>

        <div className="mt-4">
          {loading ? <LoadingSpinner /> :
           error ? <div className="text-sm text-rose-600 py-6 text-center">{error}</div> :
           rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{emptyHint || 'No matching records.'}</div> :
           (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b">
                  <tr>
                    {columns.map(c => (
                      <th key={c.key} className={`px-3 py-2 text-left font-medium text-xs text-zinc-600 ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 200).map((row, i) => (
                    <tr key={row.id || row._id || row.supplier_code || row.code || i} className="border-b last:border-0 hover:bg-zinc-50">
                      {columns.map(c => (
                        <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right tabular-nums font-mono' : ''}`}>
                          {c.format ? c.format(row[c.key], row) : (row[c.key] != null ? String(row[c.key]) : '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 200 && <div className="text-xs text-muted-foreground p-2 text-center">Showing first 200 of {rows.length} results</div>}
            </div>
           )
          }
        </div>

        {linkTo && rows.length > 0 && (
          <div className="mt-4">
            <a href={linkTo} className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="drill-open-list-link">
              Open in full list view <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
